import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { ArrowBack } from "../components/CustomIcons";
import Footer from "../components/Footer";
import MarkDownWithOption from "../components/ui/MarkDownWithOption";
import {
  getLeagueBySlug,
  updateLeagueProgressStep,
  updateLeagueRules,
} from "../components/leagueLocker/firestoreQuery";
import { useAuth } from "../contexts/AuthContext";
import { SectionLoader } from "../components/ui/SectionLoader";

const EditLeagueRulesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { leagueSlug } = useParams(); //Edit mode

  const [seasonStartup, setSeasonStartup] = useState("");
  const [draftDay, setDraftDay] = useState("");
  const [scoring, setScoring] = useState("");
  const [waiversAndFreeAgency, setWaiversAndFreeAgency] = useState("");
  const [trades, setTrades] = useState("");
  const [leagueAdditionalDetails, setLeagueAdditionalDetails] = useState("");
  const [leagueId, setLeagueId] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLeagueDetails = async () => {
      if (leagueSlug) {
        try {
          setIsLoading(true);
          const res = await getLeagueBySlug(leagueSlug);
          if (res) {
            if (res.createdBy !== user?.uid) {
              navigate("/dashboard");
              return;
            } else {
              // Populate state with fetched data
              setSeasonStartup(res.leagueRules?.seasonStartup || "");
              setDraftDay(res.leagueRules?.draftDay || "");
              setScoring(res.leagueRules?.scoring || "");
              setWaiversAndFreeAgency(
                res.leagueRules?.waiversAndFreeAgency || ""
              );
              setTrades(res.leagueRules?.trades || "");
              setLeagueAdditionalDetails(
                res.leagueRules?.leagueAdditionalDetails || ""
              );
              setLeagueId(res.id || "");
            }
          } else {
            console.error("No league found with the provided slug.");
            navigate("/dashboard");
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error fetching league:", error);
          setIsLoading(false);
          //setErrorText(error as string);
        }
      }
      setIsLoading(false);
    };
    fetchLeagueDetails();
  }, [leagueSlug]);

  const handleFormSubmit = async () => {
    // Logic to save the updated league rules
    const payload = {
      seasonStartup,
      draftDay,
      scoring,
      waiversAndFreeAgency,
      trades,
      leagueAdditionalDetails,
    };
    if (!leagueSlug) {
      console.error("League slug is invalid. Cannot save league rules.");
      return;
    }
    try {
      setIsLoading(true);
      await updateLeagueRules(leagueId, payload);
      await updateLeagueProgressStep(leagueId, "edit-rules");
      console.log("League rules updated successfully");
    } catch (error) {
      console.error("Error saving league rules:", error);
    } finally {
      setIsLoading(false);
    }
    // After saving, navigate back to the league overview or another appropriate page
    navigate(`/manage-league/${leagueSlug}`);
  };

  if (isLoading) {
    return <SectionLoader />;
  }

  return (
    <div className="pageOuterWrapper">
      <Navbar />
      <div className="contentArea otherContentArea">
        <div className="wrapper">
          <div className="backWrap">
            <Link to={`/manage-league/${leagueSlug}`}>
              <ArrowBack /> Back
            </Link>
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleFormSubmit();
          }}
        >
          <div className="wrapper editLeagueRulesPage">
            <div className="yourLeagueHeader">
              <div className="headingItem">League Rules</div>
              <div className="actionItems">
                <button className="btnStyle btnFilled" type="submit">
                  SAVE
                </button>
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">
                🗓️ <span>Season Startup</span>
              </div>
              <div className="formGroup">
                <label htmlFor="seasonStartup">
                  How is your leagues season currently setup?
                  {/* <span className="reqTxt">Required</span> */}
                </label>
                <MarkDownWithOption
                  value={seasonStartup}
                  onChange={(value) => setSeasonStartup(value ?? "")}
                />
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">
                🫡 <span>Draft Day</span>
              </div>
              <div className="formGroup">
                <label htmlFor="draftDay">
                  How will draft day work for your league?
                </label>
                <MarkDownWithOption
                  value={draftDay}
                  onChange={(value) => setDraftDay(value ?? "")}
                />
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">
                🏈 <span>Scoring</span>
              </div>
              <div className="formGroup">
                <label htmlFor="scoring">How will you keep scores?</label>
                <MarkDownWithOption
                  value={scoring}
                  onChange={(value) => setScoring(value ?? "")}
                />
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">
                📝 <span>Waivers & Free Agency</span>
              </div>
              <div className="formGroup">
                <label htmlFor="waiversAndFreeAgency">
                  Add rules around waivers & free agency here
                </label>
                <MarkDownWithOption
                  value={waiversAndFreeAgency}
                  onChange={(value) => setWaiversAndFreeAgency(value ?? "")}
                />
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">
                🔁 <span>Trades</span>
              </div>
              <div className="formGroup">
                <label htmlFor="trades">
                  Add rules around any trades or trade deadlines here
                </label>
                <MarkDownWithOption
                  value={trades}
                  onChange={(value) => setTrades(value ?? "")}
                />
              </div>
            </div>
            <div className="shadowBlk">
              <div className="blkHeading">
                ✍️ <span>Additional Details</span>
              </div>
              <div className="formGroup">
                <label htmlFor="additionalDetails">
                  Anything else your league should know?
                </label>
                <MarkDownWithOption
                  value={leagueAdditionalDetails}
                  onChange={(value) => setLeagueAdditionalDetails(value ?? "")}
                />
              </div>
            </div>
            <button className="btnStyle btnFilled btnFull" type="submit">
              SAVE
            </button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default EditLeagueRulesPage;
